import 'reflect-metadata';
import { container } from 'tsyringe';
import { ThemeService } from '@/shared/ThemeService';
import { GridViewsService } from '@/shared/grid-views/GridViewsService';
import { GlobalFilterController } from '@/features/global-filters/GlobalFilterController';
import { GlobalFilterValueService } from '@/features/global-filters/GlobalFilterValueService';
import { LicensesApiService } from '@/features/licenses/LicensesApiService';
import { DirectoryApiService } from '@/features/directory/DirectoryApiService';
import { NotificationsApiService } from '@/features/notifications/NotificationsApiService';
import { NotificationActivityViewModel } from '@/features/notifications/NotificationActivityViewModel';
import { registerAnalytics } from '@/shared/analytics/AnalyticsRegistration';
import { registerAdoption } from '@/features/adoption/AdoptionRegistration';
import { registerSurfaceAssist } from '@/shared/surfaces/assist/SurfaceAssistRegistration';
import { registerAIExplorer } from '@/features/ai-explorer/AIExplorerRegistration';
import { FieldCatalogService } from '@/shared/surfaces/FieldCatalogService';
import { ClassificationService } from '@/features/classification/ClassificationService';
import { GovernanceIssueStore } from '@/features/governance/GovernanceIssueStore';
import { UsageCategoryTaxonomyService } from '@/features/usage-categories/UsageCategoryTaxonomyService';
import { registerCategorySuggestionEngine } from '@/features/usage-categories/CategorySuggestionRegistration';
import { registerClassificationAssistEngine } from '@/features/classification/ClassificationAssistRegistration';
import { RolesAdminService } from '@/features/admin/RolesAdminService';
import { PermissionsAdminService } from '@/features/admin/PermissionsAdminService';
import { UsersAdminService } from '@/features/admin/UsersAdminService';
import { SsoAdminService } from '@/features/admin/SsoAdminService';
import { TenantsAdminService } from '@/features/admin/TenantsAdminService';
import { AppBasePathToken, AuthFetchToken, ConnectionsApiBasePathToken, ConnectionsClientToken } from '@/container/IntegrationTokens';
import { IntegrationsService } from '@/features/integrations/IntegrationsService';
import { RuntimeConfigToken, type RuntimeConfig } from '@/runtime-config/RuntimeConfig';
import { connectionsClient } from '@/shared/ConnectionsApiClient';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';
import { SurfaceRegistry } from '@/shared/surfaces/SurfaceRegistry';
import { SurfaceSignalCatalog } from '@/shared/signals/SurfaceSignalCatalog';
import { ISignalLicenseSourceToken } from '@/shared/signals/SignalTypes';
import { AI_EXPLORER_SURFACE } from '@/features/ai-explorer/AIExplorerSurface';
import { TOKEN_USAGE_SURFACE } from '@/features/token-usage/TokenUsageSurface';
import { GOVERNANCE_SURFACE } from '@/features/governance/GovernanceSurface';
import { BEHAVIOR_SURFACE } from '@/features/behavior/BehaviorSurface';
import { ADOPTION_SURFACE } from '@/features/adoption/AdoptionSurface';
import { LICENSE_COST_SIGNALS } from '@/features/licenses/LicenseSignals';
import { BEHAVIOR_SIGNALS } from '@/features/behavior/BehaviorSignals';
import { ShieldCheck } from 'lucide-react';
import { registerRecordDrawerSection } from '@/shared/surfaces/RecordDrawerExtensions';
import RecordPolicyVerdicts from '@/features/governance/RecordPolicyVerdicts';
import { TOKEN_USAGE_SIGNALS } from '@/features/token-usage/TokenUsageSignals';

let initialized = false;

/**
 * Root DI container registration.
 * Registers singletons and shared services used across the application.
 *
 * Call this once at application startup (in main.tsx), after fetching the
 * BFF's runtime-config so the RuntimeConfigToken is available to consumers.
 */
export function initializeContainer(runtimeConfig: RuntimeConfig): void {
    if (initialized) return;
    initialized = true;

    container.register<RuntimeConfig>(RuntimeConfigToken, { useValue: runtimeConfig });

    registerFeatureContributions();

    container.registerSingleton(ThemeService);
    container.registerSingleton(GridViewsService);
    container.registerSingleton(GlobalFilterController);
    container.registerSingleton(GlobalFilterValueService);

    container.registerSingleton(NotificationsApiService);
    container.register(NotificationActivityViewModel, { useClass: NotificationActivityViewModel });
    container.registerSingleton(LicensesApiService);
    container.register(ISignalLicenseSourceToken, { useToken: LicensesApiService });
    container.registerSingleton(DirectoryApiService);
    registerAnalytics(container);
    registerAdoption(container);
    registerSurfaceAssist(container);
    registerAIExplorer(container);
    container.registerSingleton(FieldCatalogService);

    container.registerSingleton(ClassificationService);
    container.registerSingleton(GovernanceIssueStore);

    container.registerSingleton(UsageCategoryTaxonomyService);
    registerCategorySuggestionEngine(container);
    registerClassificationAssistEngine(container);

    container.registerSingleton(RolesAdminService);
    container.registerSingleton(PermissionsAdminService);
    container.registerSingleton(UsersAdminService);
    container.registerSingleton(SsoAdminService);
    container.registerSingleton(TenantsAdminService);

    container.register(ConnectionsClientToken, { useValue: connectionsClient });
    container.register(AuthFetchToken, { useValue: authenticatedFetch });
    container.register(ConnectionsApiBasePathToken, { useValue: '/_connections' });
    container.register(AppBasePathToken, { useValue: '' });
    container.registerSingleton(IntegrationsService);
}

export { container };

/**
 * Feeds feature-owned data into the shared engines' registries (surface
 * definitions for the drill-in engine, signal tiles for the landing grids).
 * Features contribute here — at the composition root — so the engines under
 * src/shared stay feature-blind.
 */
function registerFeatureContributions(): void {
    container.resolve(SurfaceRegistry).register([AI_EXPLORER_SURFACE, TOKEN_USAGE_SURFACE, GOVERNANCE_SURFACE, BEHAVIOR_SURFACE, ADOPTION_SURFACE]);
    const signalCatalog = container.resolve(SurfaceSignalCatalog);
    signalCatalog.register('spend-cost', LICENSE_COST_SIGNALS);
    signalCatalog.register('behavior', BEHAVIOR_SIGNALS);
    signalCatalog.register('token-usage', TOKEN_USAGE_SIGNALS);
    registerRecordDrawerSection({ label: 'Governance', icon: ShieldCheck, component: RecordPolicyVerdicts });
}
